import React from 'react';
import ChartRenderer from './ChartRenderer';
import EChartsRenderer from './EChartsRenderer';
import './SectionRenderer.css';

/**
 * 渲染文章内容的不同类型区块
 * @param {Object} section - 内容区块
 * @param {string} lang - 当前语言
 */
const SectionRenderer = ({ section, lang }) => {
  const { type } = section;

  switch (type) {
    case 'heading':
      return renderHeading(section, lang);
    
    case 'paragraph':  // 后端使用paragraph
    case 'text':
      return renderText(section, lang);
    
    case 'image':
      return renderImage(section, lang);
    
    case 'chart':
      return renderChart(section, lang);
    
    case 'list':
      return renderList(section, lang);
    
    case 'table':
      return renderTable(section, lang);
    
    case 'quote':
      return renderQuote(section, lang);
    
    default:
      console.warn(`Unknown section type: ${type}`);
      return null;
  }
};

// 渲染标题
const renderHeading = (section, lang) => {
  const { level = 2, content, text } = section;
  const HeadingTag = `h${level}`;
  // 支持content或text字段
  const textContent = content || text;
  const displayText = typeof textContent === 'object' ? textContent[lang] : textContent;

  return (
    <HeadingTag className={`section-heading heading-${level}`}>
      {displayText}
    </HeadingTag>
  );
};

// 渲染文本段落
const renderText = (section, lang) => {
  const { content, text, html } = section;
  // 支持content或text字段
  const textContent = content || text;
  const displayText = typeof textContent === 'object' ? textContent[lang] : textContent;

  // 如果包含HTML格式，使用dangerouslySetInnerHTML
  if (html && displayText) {
    // 确保HTML内容被正确解析
    return (
      <div className="section-text">
        <p dangerouslySetInnerHTML={{ __html: displayText }} />
      </div>
    );
  }

  return (
    <div className="section-text">
      <p>{displayText}</p>
    </div>
  );
};

// 渲染图片
const renderImage = (section, lang) => {
  const { src, url, alt, caption } = section;
  // 支持src或url字段
  const imageSrc = src || url;
  const altText = typeof alt === 'object' ? alt[lang] : alt;
  const captionText = caption && (typeof caption === 'object' ? caption[lang] : caption);

  return (
    <figure className="section-image">
      <img src={imageSrc} alt={altText || ''} />
      {captionText && <figcaption>{captionText}</figcaption>}
    </figure>
  );
};

// 渲染图表
const renderChart = (section, lang) => {
  // 如果有chart_type为echarts，使用EChartsRenderer
  if (section.chart_type === 'echarts' || section.chart_id) {
    return <EChartsRenderer section={section} lang={lang} />;
  }
  
  // 否则使用原有的ChartRenderer
  return (
    <div className="section-chart">
      <ChartRenderer section={section} lang={lang} />
    </div>
  );
};

// 渲染列表
const renderList = (section, lang) => {
  const { items, ordered = false } = section;
  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <ListTag className="section-list">
      {items.map((item, index) => {
        const text = typeof item === 'object' ? item[lang] : item;
        
        // 检测是否为key-value格式（用 | 分隔，且第一部分较短）
        if (typeof text === 'string' && text.includes(' | ')) {
          const parts = text.split(' | ');
          if (parts.length >= 2 && parts[0].length < 50) {
            // 第一部分作为key加粗
            return (
              <li key={index} className="list-item-key-value">
                <strong>{parts[0]}</strong>
                <span className="separator"> | </span>
                <span>{parts.slice(1).join(' | ')}</span>
              </li>
            );
          }
        }
        
        // 检测文本是否包含HTML标签（如<strong>等）
        if (typeof text === 'string' && /<[^>]+>/.test(text)) {
          return (
            <li key={index} dangerouslySetInnerHTML={{ __html: text }} />
          );
        }
        
        return <li key={index}>{text}</li>;
      })}
    </ListTag>
  );
};

// 渲染表格
const renderTable = (section, lang) => {
  const { headers, rows, caption } = section;
  
  if (!headers || !rows) {
    console.warn('Table section missing headers or rows');
    return null;
  }

  const getHeaderText = (header) => {
    return typeof header === 'object' ? header[lang] : header;
  };

  const getCellText = (cell) => {
    return typeof cell === 'object' ? cell[lang] : cell;
  };

  return (
    <div className="section-table-wrapper">
      <table className="section-table">
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index}>{getHeaderText(header)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{getCellText(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {caption && (
        <div className="table-caption">
          {typeof caption === 'object' ? caption[lang] : caption}
        </div>
      )}
    </div>
  );
};

// 渲染引用块
const renderQuote = (section, lang) => {
  const { content, author } = section;
  const text = typeof content === 'object' ? content[lang] : content;
  const authorText = author && (typeof author === 'object' ? author[lang] : author);

  return (
    <blockquote className="section-quote">
      <p>{text}</p>
      {authorText && <cite>— {authorText}</cite>}
    </blockquote>
  );
};

export default SectionRenderer;
